import { useQuery } from '@tanstack/react-query'
import { strapiGet } from '@/lib/strapi'
import { useAppStore } from '@/lib/store/useAppStore'

export interface GuestSuggestion {
  name: string
  email: string
  phone: string
}

// Fetch recent bookings once per session and filter client-side.
// Strapi 5 REST API does not support filtering on component fields.
function useRecentGuests(restaurantId: string | null) {
  return useQuery({
    queryKey: ['recent-guests', restaurantId],
    queryFn: async () => {
      const resp = await strapiGet<any[]>('/api/bookings', {
        filters: { restaurant: { documentId: { $eq: restaurantId } } },
        populate: { guest: { fields: ['name', 'email', 'phone'] } },
        fields: ['id'],
        pagination: { pageSize: 500 },
        sort: ['createdAt:desc'],
      })

      const seen = new Set<string>()
      const guests: GuestSuggestion[] = []
      for (const booking of resp.data ?? []) {
        const g = booking.guest
        if (!g?.name) continue
        const key = `${g.name.toLowerCase()}|${(g.email ?? '').toLowerCase()}`
        if (seen.has(key)) continue
        seen.add(key)
        guests.push({ name: g.name, email: g.email ?? '', phone: g.phone ?? '' })
      }
      return guests
    },
    enabled: !!restaurantId,
    staleTime: 5 * 60_000,
  })
}

export function useGuestSuggestions(search?: string) {
  const restaurantId = useAppStore((s) => s.restaurantId)
  const { data: allGuests = [] } = useRecentGuests(restaurantId)

  if (!search || search.length < 2) return { data: [] }

  const q = search.toLowerCase()
  const matches = allGuests.filter(
    (g) =>
      g.name.toLowerCase().includes(q) ||
      g.email.toLowerCase().includes(q) ||
      g.phone.includes(q),
  )

  return { data: matches.slice(0, 6) }
}
